import ReactECharts from 'echarts-for-react';
import type { StyleConfig } from '../../../../types/bi';
import { useEChartsTheme } from '../../hooks/useEChartsTheme';
import { buildChartClickHandler } from '../../utils/drilldown';
import type { DrilldownEvent } from '../../utils/drilldown';

interface HeatMapProps {
  data: { columns: string[]; rows: Record<string, unknown>[] };
  dimensions: string[];
  measures: { alias: string }[];
  style?: StyleConfig;
  onDrillDown?: (event: DrilldownEvent) => void;
}

export default function HeatMap({ data, dimensions, measures, style, onDrillDown }: HeatMapProps) {
  const theme = useEChartsTheme();

  const xDim = dimensions[0] || data.columns[0];
  const yDim = dimensions[1] || data.columns[1];
  const measureKey = measures[0]?.alias || data.columns[2];

  // Extract unique axis labels
  const xLabels = [...new Set(data.rows.map((r) => String(r[xDim] ?? '')))];
  const yLabels = [...new Set(data.rows.map((r) => String(r[yDim] ?? '')))];

  // Build heat data: [xIndex, yIndex, value]
  let min = Infinity;
  let max = -Infinity;
  const heatData = data.rows.map((r) => {
    const val = Number(r[measureKey]) || 0;
    if (val < min) min = val;
    if (val > max) max = val;
    return [xLabels.indexOf(String(r[xDim] ?? '')), yLabels.indexOf(String(r[yDim] ?? '')), val];
  });
  if (!isFinite(min)) min = 0;
  if (!isFinite(max)) max = 1;

  const option = {
    tooltip: {
      position: 'top' as const,
      formatter: (params: { value: number[] }) => {
        const [xi, yi, v] = params.value;
        return `${xLabels[xi]} / ${yLabels[yi]}: ${v}`;
      },
    },
    grid: { left: '3%', right: '8%', bottom: '12%', top: '3%', containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: xLabels,
      name: style?.axis_labels?.x,
      splitArea: { show: true },
    },
    yAxis: {
      type: 'category' as const,
      data: yLabels,
      name: style?.axis_labels?.y,
      splitArea: { show: true },
    },
    visualMap: {
      min,
      max,
      calculable: true,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 0,
      inRange: {
        color: style?.colours?.length
          ? style.colours
          : [
              '#313695',
              '#4575b4',
              '#74add1',
              '#abd9e9',
              '#fee090',
              '#fdae61',
              '#f46d43',
              '#d73027',
            ],
      },
    },
    series: [
      {
        type: 'heatmap' as const,
        data: heatData,
        label: { show: data.rows.length <= 100 },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      },
    ],
  };

  const events = buildChartClickHandler(dimensions, onDrillDown);

  return (
    <ReactECharts
      option={option}
      theme={theme}
      style={{ height: '100%', width: '100%' }}
      onEvents={events}
    />
  );
}
